import React from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import useDropdown from 'Hooks/useDropdown';

import FilterIcon from 'Assets/icons/filter.svg';
import XMarkIcon from 'Assets/icons/xmark.svg';

export default function TableFilterModal({
    children,
    noActions,
    onApply,
    onCancel = null,
    showApply = true,
    showCancel = false,
    customIconStyle = '',
    modalBodyClassNames,
    cancelText = null,
    applyText = null,
    headerText = null,
    closeOnAction = true,
    contentMaxHeight = '60vh',
    isActive = false,
    overflowYVisible = false,
    // Opt-in escape hatch for the PM redesign wireframe, which draws Filter as a labelled
    // outline button in the controls row rather than the round icon used on every other
    // table. Both default to undefined, so every existing caller keeps the icon trigger
    // byte for byte.
    triggerLabel,
    triggerClassName,
    // Two more opt-in slots for the PM redesign wireframe's filter: actions beside the title
    // ("Select all · Clear all") and a status beside Apply ("N selected"). Both default to
    // undefined, so every other caller keeps the header and footer it has.
    headerActions,
    footerStatus,
    // `sectioned` is the wireframe's filter card: a hairline under the header and above the
    // footer dividing the panel into three bands, its own padding, and a rectangular Apply
    // rather than the pill. Defaults to the existing look, which every other table keeps.
    // The panel is portalled to <body>, so a container class on the page cannot reach it —
    // this is how the Project Plan's filter gets the wireframe geometry.
    sectioned = false,
}) {
    const sectionPadding = sectioned ? 'tw-px-3.5 tw-py-3' : null;
    const { triggerRef, dropdownRef, isOpen, close, toggle } = useDropdown();

    const onApplyClick = () => {
        if (closeOnAction) {
            close();
        }
        typeof onApply === 'function' && onApply();
    };

    const onCancelClick = () => {
        if (closeOnAction) {
            close();
        }
        typeof onCancel === 'function' && onCancel();
    };

    return (
        <div>
            <button
                type="button"
                className={
                    triggerClassName
                        ? classNames(triggerClassName, customIconStyle, { '!tw-border-blue !tw-text-blue': isActive })
                        : `table__icon-btn ${isActive ? '!tw-bg-green-500 !tw-border-green-500' : 'secondary'}
                ${customIconStyle}`
                }
                ref={triggerRef}
                onClick={toggle}
            >
                <FilterIcon className="tw-w-3.5 tw-h-3.5" />
                {triggerLabel}
            </button>
            {createPortal(
                <div
                    className={classNames(
                        'table__filter-modal tw-flex tw-flex-col',
                        { hidden: !isOpen, 'tw-rounded-xl tw-border tw-border-[#e3e6ea] tw-overflow-hidden': sectioned },
                        modalBodyClassNames,
                    )}
                    ref={dropdownRef}
                    style={{ zIndex: 1000 }}
                >
                    {/* The close cross is absolutely positioned into the top-right corner, which is
                        where the sectioned header puts its actions — the two collide. The wireframe
                        has no cross there either; its note reads "Click Filter again to hide", and
                        the trigger already toggles. */}
                    {!sectioned && (
                        <button type="button" className="table__filter-modal__close-btn" onClick={close}>
                            <span aria-hidden="true">
                                <XMarkIcon className="tw-w-2.5 tw-h-2.5" />
                            </span>
                        </button>
                    )}
                    <div className={classNames('tw-shrink-0', sectioned ? `${sectionPadding} tw-border-b tw-border-[#e3e6ea]` : 'tw-pt-4 tw-px-4')}>
                        <div className={classNames({ 'tw-flex tw-items-center tw-justify-between tw-gap-x-4': headerActions })}>
                            <div className={classNames('table__filter-modal__title', { '!tw-mb-0 !tw-text-blue': sectioned })}>
                                {headerText || $t('Filters')}
                            </div>
                            {headerActions}
                        </div>
                    </div>
                    <div
                        className={classNames('tw-flex-1 tw-min-h-0', sectioned ? sectionPadding : 'tw-px-4 tw-pb-4', {
                            'tw-overflow-y-visible': overflowYVisible,
                            'tw-overflow-y-auto': !overflowYVisible,
                        })}
                        style={{ maxHeight: contentMaxHeight }}
                    >
                        {children}
                    </div>
                    {!noActions && (
                        <div
                            className={classNames(
                                'tw-flex tw-shrink-0',
                                sectioned ? `${sectionPadding} tw-border-t tw-border-[#e3e6ea]` : 'tw-px-2.5 tw-py-3',
                                footerStatus ? 'tw-items-center tw-justify-between' : 'tw-justify-end',
                            )}
                        >
                            {footerStatus}
                            {/* Kept together so a status on the left pushes the pair to the right as
                                one unit rather than spreading Cancel and Apply apart. */}
                            <div className="tw-flex tw-items-center">
                                {showCancel && (
                                    <button type="button" className="tw-mr-2" onClick={() => onCancelClick()}>
                                        {cancelText || $t('Cancel')}
                                    </button>
                                )}
                                {showApply && (
                                    <button
                                        type="button"
                                        className={classNames('table__filter-modal__apply-btn', {
                                            '!tw-h-[34px] !tw-rounded-lg !tw-px-4 !tw-text-[13px] !tw-font-medium': sectioned,
                                        })}
                                        onClick={() => onApplyClick()}
                                    >
                                        {applyText ?? $t('Apply')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
}
